import { getSupabase } from "@/lib/supabase/client";
import { clearSynced, loadQueue, type OfflineMutation } from "./queue";
import { createCheckIn } from "@/features/check-in/api/check-in-api";
import {
  completeAssignment,
  startAssignment,
} from "@/features/experiments/api/experiments-api";

/**
 * Attempts to flush offline mutations when online + authenticated.
 */
export async function flushOfflineQueue(): Promise<number> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return 0;
  }

  const supabase = getSupabase();
  if (!supabase) return 0;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return 0;

  const queue = loadQueue();
  if (queue.length === 0) return 0;

  const synced: string[] = [];

  for (const item of queue) {
    try {
      const ok = await processMutation(item);
      if (ok) synced.push(item.id);
    } catch {
      // leave in queue
    }
  }

  if (synced.length > 0) {
    clearSynced(synced);
  }
  return synced.length;
}

async function processMutation(item: OfflineMutation): Promise<boolean> {
  const payload = item.payload as Record<string, unknown>;

  switch (item.type) {
    case "CREATE_CHECK_IN": {
      const mood = payload.mood;
      if (typeof mood !== "number") return false;
      const result = await createCheckIn({
        mood: mood as 0 | 1 | 2 | 3 | 4,
        energy: (payload.energy as number | null) ?? null,
        stress: (payload.stress as number | null) ?? null,
        ...(typeof payload.note === "string" ? { note: payload.note } : {}),
        clientCreatedAt:
          typeof payload.clientCreatedAt === "string"
            ? payload.clientCreatedAt
            : item.createdAt,
        idempotencyKey:
          typeof payload.idempotencyKey === "string"
            ? payload.idempotencyKey
            : item.id,
      });
      return result.ok;
    }
    case "START_EXPERIMENT": {
      const assignmentId = payload.assignmentId;
      if (typeof assignmentId !== "string") return false;
      return startAssignment(assignmentId);
    }
    case "COMPLETE_EXPERIMENT":
    case "CREATE_REFLECTION": {
      const assignmentId = payload.assignmentId;
      if (typeof assignmentId !== "string") return false;
      return completeAssignment(assignmentId, {
        completed: Boolean(payload.completed),
        afterMood:
          typeof payload.afterMood === "number"
            ? (payload.afterMood as 0 | 1 | 2 | 3 | 4)
            : null,
        afterEnergy:
          typeof payload.afterEnergy === "number" ? payload.afterEnergy : null,
        repeatIntent:
          typeof payload.repeatIntent === "number"
            ? (payload.repeatIntent as 0 | 1 | 2)
            : null,
        reflection:
          typeof payload.reflection === "string"
            ? payload.reflection
            : undefined,
      });
    }
    default:
      return false;
  }
}
